import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dateLocale } from '../../i18n/i18n';
import { MISC_LABELS, tagLabel, roleLabel } from '../../constants/labels';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from "react-router-dom";
import ConfirmationModal from '../../components/modals/confirmation';
import SetTempEditOrderModal from '../../components/modals/orderEditTemp';
import SetOrderModal from '../../components/modals/orderSet';
import { SkeletonRows } from '../../components/skeleton';
import { deleteJob, getJobById, getJobs, updateJob } from '../../services/jobs';
import { onMutateSuccess, onQuerySuccess } from '../../utils/queryResponse';
import { isUserAdmin } from '../../utils/roleHelpers';
import { rsdFormat, displayAddress1, getFullAddress, getCivicAddress } from '../../utils/helper';
import Avatar from '../../components/avatar';
import { invalidateJobStats } from '../../utils/invalidateStats';
import JobsFilter from './jobsFilter';
import InvoiceSharePanel from '../../components/invoiceSharePanel';
import AddToRouteDrawer from '../../components/modals/addToRouteDrawer';
import { matchesActivityFilter } from '../../utils/routeActivity';
import { JOB_STATUS, JOB_STATUS_ORDER, STATUS_GROUPS, getStatusMeta, normalizeStatus } from '../../constants/jobStatus';

/** PENDING payment whose due date has passed (the API stamps paymentDueDate on completion). */
const isInvoiceOverdue = (job) => job?.paymentStatus === 'PENDING' && !!job?.paymentDueDate && new Date(job.paymentDueDate) < new Date();

/* ── icons ── */
const IcPlus = () => <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const IcPin = () => <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>;
const IcChevR = () => <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>;
const IcWarn = () => <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
const IcClock = () => <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const IcPeople = () => <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>;
const IcRoute = () => <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="19" r="3"/><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"/><circle cx="18" cy="5" r="3"/></svg>;
const IcCheck = () => <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;

/* ── status mapping ──
   Labels/colors come from the single source of truth in
   `src/constants/jobStatus.js`. `normalizeStatus` transparently maps a job's
   raw status — whether it's already a new canonical value or one of the old
   pre-redesign values still on existing records — to the new terminology, so
   this works correctly for both without a backend migration. `tone` (grey/
   amber/green/cancelled) drives both the row accent (`s-*`) and the badge (`b-*`). */
const TONE_TO_CLS = { grey: 's-grey', amber: 's-amber', green: 's-green', cancelled: 's-cancelled' };
const TONE_TO_BADGE = { grey: 'b-grey', amber: 'b-amber', green: 'b-green', cancelled: 'b-cancelled' };

const statusInfo = (status) => {
    const meta = getStatusMeta(status);
    return { cls: TONE_TO_CLS[meta.tone], badge: TONE_TO_BADGE[meta.tone], label: meta.label, chip: normalizeStatus(status) };
};

const fmtTime = (dt) => {
    if (!dt) return null;
    return new Date(dt).toLocaleTimeString(dateLocale(), { hour: '2-digit', minute: '2-digit' });
};

const fmtDateLabel = (d) =>
    new Date(d).toLocaleDateString(dateLocale(), { day: 'numeric', month: 'long' });

const dayLabel = (d, tr) => {
    const now = new Date(); now.setHours(0, 0, 0, 0);
    const tm = new Date(now); tm.setDate(tm.getDate() + 1);
    const dd = new Date(d); dd.setHours(0, 0, 0, 0);
    if (dd.getTime() === now.getTime()) return tr('jobs.today');
    if (dd.getTime() === tm.getTime()) return tr('jobs.tomorrow');
    return new Date(d).toLocaleDateString(dateLocale(), { weekday: 'long' });
};

// The time the customer expects the job to start: the pickup for a
// PICKUP_FROM_HOME job, the drop-off for a CUSTOMER_DELIVERS one. This is the
// ONE timestamp the row displays, sorts on, groups by and judges "overdue"
// against — showing dispatchTime while flagging on deliveredByCustomerTime
// was how a job could read "14:00 tomorrow" next to a red Overdue badge.
const getScheduledTime = (job) =>
    job.pickupType === 'CUSTOMER_DELIVERS' ? job.deliveredByCustomerTime : job.pickupTime;

// Display/sort anchor: scheduled start, else the later stage times.
const getAnchorTime = (job) => getScheduledTime(job) || job.dispatchTime || job.completionTimeActual || null;

const getPrimaryTime = (job) => new Date(getAnchorTime(job) || job.createdAt);

// A job with none of these has no real schedule/activity anchor yet — it
// belongs in the "Unscheduled" bucket, not a calendar day group (getPrimaryTime
// falls back to createdAt for display/sort purposes only, which is not a
// real anchor and must not be used to decide day-group membership).
const hasTimeAnchor = (job) => !!getAnchorTime(job);

const getPrimaryAssignee = (job) =>
    job.pickupAssignedTo || job.cleaningAssignedTo || job.deliveryAssignedTo || null;

// Built from STATUS_GROUPS (constants/jobStatus.js) so every bucket covers a
// canonical status and all of its legacy aliases together, mirroring the
// backend's "needs attention" logic (overview.ts) exactly — a job never
// drops out of these buckets just because it's stored under the new status
// name. PICKUP_STAGE_STATUSES = pre-arrival + arrived; which timestamp
// "overdue" counts depends on pickupType — a CUSTOMER_DELIVERS job has no
// pickupTime, it's checked against deliveredByCustomerTime instead.
const PICKUP_STAGE_STATUSES = [...STATUS_GROUPS.NEW, ...STATUS_GROUPS.CONFIRMED, ...STATUS_GROUPS.SCHEDULED, ...STATUS_GROUPS.ARRIVED];
const CLEANING_STAGE_STATUSES = [...STATUS_GROUPS.IN_PROGRESS, ...STATUS_GROUPS.FINISHING];
const DELIVERY_STAGE_STATUSES = [...STATUS_GROUPS.READY];

// Overdue = the scheduled start has passed and nobody has gone to collect the
// job yet. A job whose time is in the future is never overdue, and neither is
// one that has already moved on: once the driver is on site (Arrived) the
// pickup is happening, so ARRIVED is deliberately NOT in this list even though
// it is part of PICKUP_STAGE_STATUSES for assignment purposes.
// Mirrors evaluateSla()'s PRE_PICKUP in slaRules.ts — keep the two in step.
const OVERDUE_ELIGIBLE_STATUSES = [...STATUS_GROUPS.NEW, ...STATUS_GROUPS.CONFIRMED, ...STATUS_GROUPS.SCHEDULED];
const isOverdue = (job) => {
    if (!OVERDUE_ELIGIBLE_STATUSES.includes(job.status)) return false;
    const due = getScheduledTime(job);
    return !!due && new Date(due) < new Date();
};

// "Waiting" = every open (not completed/cancelled) stage, same as
// isUnassigned's stages combined.
const WAITING_ELIGIBLE_STATUSES = [...PICKUP_STAGE_STATUSES, ...CLEANING_STAGE_STATUSES, ...DELIVERY_STAGE_STATUSES];

const isUnassigned = (job) => {
    if (PICKUP_STAGE_STATUSES.includes(job.status)) return !job.pickupAssignedTo;
    if (CLEANING_STAGE_STATUSES.includes(job.status)) return !job.cleaningAssignedTo;
    if (DELIVERY_STAGE_STATUSES.includes(job.status)) return !job.deliveryAssignedTo;
    return false;
};

const WAITING_THRESHOLD_MS = 24 * 60 * 60 * 1000;

/* Date filters are { start, end, status }. No start/end = "All jobs": the
   complete master list, whatever a job's dates or status. That is the
   default — a job must never be invisible here just because of when it was
   created or scheduled; the Calendar is the date view. */
const allTimeRange = () => ({ start: null, end: null });

/* Just today (00:00 → 23:59:59) — used by the Overview "Jobs today" card. */
const todayRange = () => {
    const s = new Date(); s.setHours(0, 0, 0, 0);
    const e = new Date(); e.setHours(23, 59, 59, 999);
    return { start: s, end: e };
};

// In "All jobs" mode the history below today is previewed to this many jobs
// (most recent first) behind a "Show older jobs" button, so a long-running
// tenant's page stays fast while nothing is actually unreachable.
const PAST_PREVIEW_COUNT = 30;

const CHIP_KEYS = [
    'all',
    ...JOB_STATUS_ORDER,
    JOB_STATUS.CANCELLED,
];

// Attention-context banner keys — labels are resolved via t() at render time.
const ATTENTION_I18N = {
    unassigned: { label: 'jobs.attentionUnassigned', hint: 'jobs.attentionUnassignedHint' },
    waiting: { label: 'jobs.attentionWaiting', hint: 'jobs.attentionWaitingHint' },
    notes: { label: 'jobs.attentionNotes', hint: 'jobs.attentionNotesHint' },
    reschedule: { label: 'jobs.attentionReschedule', hint: 'jobs.attentionRescheduleHint' },
};
const ATTENTION_KEYS = ['unassigned', 'waiting', 'notes', 'reschedule'];

// Jobs flagged from a route stop ("customer unavailable") until the office
// gives them a new time.
const needsReschedule = (job) => !!job.needsRescheduleSince;

const JobsPage = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const queryJobId = searchParams.get("jobId");
    // When opened from a client's "Total Jobs" stat we filter to that client and
    // widen the date range so their whole job history shows (not just this week).
    const queryClientId = searchParams.get("clientId");
    const queryClientName = searchParams.get("clientName");
    // When opened from the Overview "unpaid invoices" card we filter the list to
    // jobs still awaiting payment (paymentStatus === 'PENDING').
    const queryPaymentStatus = searchParams.get("paymentStatus");
    // When opened from one of the other Overview "Needs your attention" cards
    // (?attention=unassigned|overdue|waiting|notes) we filter to that specific
    // issue. 'overdue' reuses the overdueOnly toggle below; the rest are handled
    // via attentionFilter.
    const queryAttention = searchParams.get("attention");
    // When opened from the Overview "Jobs today" card (?date=today) we narrow
    // the date range down to just today instead of the default current week.
    const queryDate = searchParams.get("date");
    const [isLoadingQueryJob, setIsLoadingQueryJob] = useState(false);

    const [orderModal, setOrderModal] = useState(false);
    const [editJobModal, setEditJobModal] = useState(false);
    const [confirmationModal, setConfirmationModal] = useState(false);

    const [selectedJob, setSelectedJob] = useState({});
    const [selectedJobForDeletion, setSelectedJobForDeletion] = useState({});
    const [activeChip, setActiveChip] = useState('all');
    // When on, the list below only shows overdue pickups (toggled via the alert banner).
    const [overdueOnly, setOverdueOnly] = useState(false);
    // Free-text search. A client context (?clientId) is applied server-side,
    // not as a name search — a name that doesn't match the job's client
    // record exactly used to hide the very jobs the link was opened for.
    const [search, setSearch] = useState('');
    // "All jobs" mode: expand the history below today past its preview.
    const [showAllPast, setShowAllPast] = useState(false);
    // Only show jobs awaiting payment (set when arriving from the Overview
    // "unpaid invoices" card via ?paymentStatus=PENDING).
    const [paymentFilter, setPaymentFilter] = useState(
        queryPaymentStatus === 'PENDING' ? 'PENDING' : null
    );
    // Only show jobs matching one of the other Overview attention cards
    // (set when arriving via ?attention=unassigned|waiting|notes).
    const [attentionFilter, setAttentionFilter] = useState(
        ATTENTION_KEYS.includes(queryAttention) ? queryAttention : null
    );
    const [alert, setAlert] = useState();
    const [invoiceToSend, setInvoiceToSend] = useState(null);

    // Route planning: tick jobs, then add them to a route plan for a day.
    // Arriving from the Route Plan page (?select=1&routeDate=…&plan=…) opens
    // selection mode straight away, pointed at that day / plan.
    const querySelect = searchParams.get('select') === '1';
    const queryRouteDate = searchParams.get('routeDate');
    const queryPlan = searchParams.get('plan');
    const [selectMode, setSelectMode] = useState(querySelect);
    const [selectedIds, setSelectedIds] = useState([]);
    const [routeDrawer, setRouteDrawer] = useState(false);
    useEffect(() => { if (querySelect) setSelectMode(true); }, [querySelect]);
    const toggleSelect = (id) => setSelectedIds(ids => (ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]));
    const exitSelectMode = () => { setSelectMode(false); setSelectedIds([]); };

    const openInvoice = (event, job) => {
        event.stopPropagation();
        const clientName = [job.client?.firstName, job.client?.lastName].filter(Boolean).join(' ') || t('jobs.client');
        setInvoiceToSend({ jobId: job.id, clientName, email: job.client?.email || '', phone: job.client?.phone || '' });
    };

    // Default: every job ("All jobs"). From the Overview "Jobs today" card,
    // narrow down to just today instead. Client / unpaid / attention contexts
    // keep the full list so nothing they point at can fall outside the range.
    const [filter, setFilter] = useState(() => ({
        ...(queryDate === 'today' ? todayRange() : allTimeRange()),
        status: '',
        activity: '',
        needsReschedule: false,
    }));

    // The initializers above only run on mount — if we're ALREADY on /jobs when the
    // client link is clicked (e.g. from the create-job drawer), only the URL params
    // change, so re-apply the client context whenever they do.
    useEffect(() => {
        if (!queryClientId) return;
        setSearch('');
        setFilter((f) => ({ ...f, ...allTimeRange() }));
    }, [queryClientId, queryClientName]);

    // Same as above for the unpaid-invoices link: if we're already on /jobs when
    // the Overview card is clicked, only the URL changes — re-apply the context.
    useEffect(() => {
        if (queryPaymentStatus !== 'PENDING') return;
        setPaymentFilter('PENDING');
        setAttentionFilter(null);
        setOverdueOnly(false);
        setActiveChip('all');
        setFilter((f) => ({ ...f, ...allTimeRange() }));
    }, [queryPaymentStatus]);

    // Same as above for the other Overview "Needs your attention" cards: if
    // we're already on /jobs when one is clicked, only the URL changes — re-apply
    // the matching filter. 'overdue' reuses the overdueOnly toggle so it shares
    // the exact same "overdue" definition as the in-page banner below.
    useEffect(() => {
        if (!queryAttention) return;
        setSearch('');
        setActiveChip('all');
        setPaymentFilter(null);
        setFilter((f) => ({ ...f, ...allTimeRange() }));
        if (queryAttention === 'overdue') {
            setAttentionFilter(null);
            setOverdueOnly(true);
        } else if (ATTENTION_KEYS.includes(queryAttention)) {
            setOverdueOnly(false);
            setAttentionFilter(queryAttention);
        }
    }, [queryAttention]);

    // Same as above for the "Jobs today" Overview card: if we're already on
    // /jobs when it's clicked, only the URL changes — re-apply the today filter.
    useEffect(() => {
        if (queryDate !== 'today') return;
        setSearch('');
        setActiveChip('all');
        setPaymentFilter(null);
        setAttentionFilter(null);
        setOverdueOnly(false);
        setFilter((f) => ({ ...f, ...todayRange() }));
    }, [queryDate]);

    // Clear the client context: reset search and drop the URL params (the
    // "All jobs" range is the default, so nothing else changes).
    const clearClientContext = () => {
        setSearch('');
        navigate('/jobs');
    };

    // Clear the unpaid filter and drop the URL param.
    const clearPaymentContext = () => {
        setPaymentFilter(null);
        navigate('/jobs');
    };

    // Clear an active "needs your attention" filter (unassigned/waiting/notes)
    // and drop the URL param.
    const clearAttentionContext = () => {
        setAttentionFilter(null);
        navigate('/jobs');
    };

    // Toggle the overdue-only view. Entering it clears the search / status chip /
    // client context (keeping the loaded date range) so ALL overdue pickups show —
    // otherwise the banner counts 4 while a client search hides 3 of them.
    const toggleOverdueView = () => {
        if (overdueOnly) { setOverdueOnly(false); return; }
        setOverdueOnly(true);
        setSearch('');
        setActiveChip('all');
        setAttentionFilter(null);
        if (queryClientId || queryClientName) navigate('/jobs', { replace: true });
    };

    const [allJobs, setAllJobs] = useState([]);

    const { data, isFetching, isLoading } = useQuery(
        ['jobs', filter.start?.getTime() ?? null, filter.end?.getTime() ?? null, filter.status || '', queryClientId || '', !!filter.needsReschedule],
        () => getJobs({ start: filter.start, end: filter.end, status: filter.status, clientId: queryClientId || undefined, needsReschedule: !!filter.needsReschedule })
    );

    const queryClient = useQueryClient();

    const { mutate: mutateDelete } = useMutation(deleteJob, {
        onSuccess: onMutateSuccess(() => {
            invalidateJobStats(queryClient);
        }, isUserAdmin() ? t('jobs.jobDeletedSuccess') : t('jobs.jobDeleteRequestSent'))
    });

    const { mutate: mutateUpdate } = useMutation(updateJob, {
        onSuccess: onMutateSuccess((result, code) => {
            if (code !== 200) { setAlert(result); setEditJobModal(true); }
            invalidateJobStats(queryClient);
        })
    });

    useEffect(() => {
        if (queryJobId) {
            setIsLoadingQueryJob(true);
            getJobById(queryJobId)
                .then(d => { if (d.code == 200) handleJobClick(d.message); setIsLoadingQueryJob(false); })
                .catch(() => setIsLoadingQueryJob(false));
        }
    }, [queryJobId]);

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            setAllJobs(Array.isArray(result) ? result : []);
        });
    }, [data]);

    const handleJobClick = (job) => { setSelectedJob(job); setEditJobModal(true); };
    const handleDeleteJob = () => mutateDelete(selectedJobForDeletion.id);

    /* ── derived data ── */
    const chipMatches = (job) => {
        if (activeChip === 'all') return true;
        return normalizeStatus(job.status) === activeChip;
    };

    const searchMatches = (job) => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        // Match like the Overview global search: Job#, Tag#, client and status.
        const badges = Array.isArray(job.badges)
            ? job.badges.map(b => (b?.badgeNo != null ? `t#${b.badgeNo} b#${b.badgeNo} ${b.badgeNo}` : '')).join(' ')
            : '';
        const haystack = [
            job.id != null ? `job#${job.id}` : '',
            job.id != null ? String(job.id) : '',
            badges,
            job.client?.firstName,
            job.client?.lastName,
            job.client ? getFullAddress(job.client) : '',
            job.client?.phone,
            job.client?.email,
            statusInfo(job.status).label,
            getPrimaryAssignee(job)?.fullName,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(q);
    };

    // Overdue (computed before matches so the overdue-only filter can no-op when
    // nothing is overdue anymore — otherwise the banner disappears with the filter stuck on)
    const overdueJobs = allJobs.filter(isOverdue);

    // Unpaid subset (for the context banner + chip counts when the filter is on).
    const paymentMatches = (job) => paymentFilter !== 'PENDING' || job.paymentStatus === 'PENDING';
    const unpaidJobs = allJobs.filter(j => j.paymentStatus === 'PENDING');
    const unpaidTotal = unpaidJobs.reduce((sum, j) => sum + Number(j.price || 0), 0);

    // Matches one of the other Overview "Needs your attention" cards
    // (unassigned / waiting > 24h / without notes). 'overdue' isn't handled
    // here — it reuses the overdueOnly toggle below instead.
    const attentionMatches = (job) => {
        if (!attentionFilter) return true;
        if (attentionFilter === 'unassigned') return isUnassigned(job);
        if (attentionFilter === 'waiting') {
            if (!WAITING_ELIGIBLE_STATUSES.includes(job.status)) return false;
            const lastChange = new Date(job.updatedAt || job.createdAt);
            return Date.now() - lastChange.getTime() > WAITING_THRESHOLD_MS;
        }
        if (attentionFilter === 'notes') return WAITING_ELIGIBLE_STATUSES.includes(job.status) && (!job.note || !job.note.trim());
        if (attentionFilter === 'reschedule') return needsReschedule(job);
        return true;
    };

    const matches = (job) =>
        chipMatches(job) && searchMatches(job) && paymentMatches(job) && attentionMatches(job) &&
        matchesActivityFilter(job, filter.activity) &&
        (!filter.needsReschedule || needsReschedule(job)) &&
        (!overdueOnly || overdueJobs.length === 0 || isOverdue(job));

    const selectedJobs = allJobs.filter(j => selectedIds.includes(j.id));

    const filteredSorted = allJobs
        .filter(matches)
        .sort((a, b) => getPrimaryTime(a) - getPrimaryTime(b));

    // Total value of the jobs currently shown (respects date range, status chip and search).
    const listTotal = filteredSorted.reduce((sum, j) => sum + Number(j.price || 0), 0);

    // Group by calendar day. Only jobs with a real time anchor — a job with
    // none would otherwise land here via getPrimaryTime's createdAt fallback
    // AND in noTimeJobs below, rendering it twice.
    const dayGroups = filteredSorted.filter(hasTimeAnchor).reduce((acc, job) => {
        const key = new Date(getPrimaryTime(job)).toDateString();
        if (!acc[key]) acc[key] = { date: getPrimaryTime(job), jobs: [] };
        acc[key].jobs.push(job);
        return acc;
    }, {});
    const sortedGroups = Object.values(dayGroups).sort((a, b) => a.date - b.date);

    // Jobs with no time anchor
    const noTimeJobs = allJobs.filter(j => !hasTimeAnchor(j) && matches(j));

    // Reading order. A bounded range (Today / This week / custom) reads
    // chronologically. "All jobs" reads as a work list: today and upcoming
    // days first, then unscheduled, then history newest-first — with the
    // history previewed to PAST_PREVIEW_COUNT jobs until expanded.
    const allTime = !filter.start && !filter.end;
    const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
    const upcomingGroups = allTime ? sortedGroups.filter(g => g.date >= todayStart) : sortedGroups;
    const pastGroups = allTime ? sortedGroups.filter(g => g.date < todayStart).reverse() : [];
    const pastTotal = pastGroups.reduce((s, g) => s + g.jobs.length, 0);
    let visiblePastGroups = pastGroups;
    if (allTime && !showAllPast) {
        visiblePastGroups = [];
        let shown = 0;
        for (const g of pastGroups) {
            if (shown >= PAST_PREVIEW_COUNT) break;
            visiblePastGroups.push(g);
            shown += g.jobs.length;
        }
    }
    const hiddenPastCount = pastTotal - visiblePastGroups.reduce((s, g) => s + g.jobs.length, 0);

    // Chip counts — when the unpaid filter is on, count only the unpaid subset so
    // the status tabs match what the list is actually showing.
    const chipBase = paymentFilter === 'PENDING' ? unpaidJobs : allJobs;
    const chipCounts = { all: chipBase.length };
    CHIP_KEYS.forEach((key) => {
        if (key === 'all') return;
        chipCounts[key] = chipBase.filter(j => normalizeStatus(j.status) === key).length;
    });

    // Today stats (right rail)
    const todS = new Date(); todS.setHours(0, 0, 0, 0);
    const todE = new Date(); todE.setHours(23, 59, 59, 999);
    const todayAll = allJobs.filter(j => { const t = getPrimaryTime(j); return t >= todS && t <= todE; });
    const todayDone = todayAll.filter(j => j.status === 'COMPLETED');
    const todayValue = todayAll.reduce((s, j) => s + Number(j.price || 0), 0);
    const todayUnpaid = todayAll
        .filter(j => j.paymentStatus === 'PENDING')
        .reduce((s, j) => s + Number(j.price || 0), 0);

    // Team on shift
    const teamMap = {};
    todayAll.forEach(j => {
        const who = getPrimaryAssignee(j);
        if (!who) return;
        if (!teamMap[who.id]) teamMap[who.id] = { ...who, count: 0 };
        teamMap[who.id].count++;
    });
    const teamOnShift = Object.values(teamMap).sort((a, b) => b.count - a.count);

    const isGlobalLoading = isLoading || isLoadingQueryJob;

    /* ── Mobile card ── */
    const MobileJobCard = ({ job }) => {
        const si = statusInfo(job.status);
        const timeStr = fmtTime(getAnchorTime(job));
        const assignee = getPrimaryAssignee(job);
        const overdue = isOverdue(job);
        const badgeNos = Array.isArray(job.badges) ? job.badges.map(b => tagLabel(b)).filter(Boolean) : [];
        const street = job.client ? getCivicAddress(job.client) : '';
        const aptDetail = job.client ? (displayAddress1(job.client) || '') : '';
        const selected = selectMode && selectedIds.includes(job.id);
        return (
            <div className={`job ${si.cls}${selected ? ' selected' : ''}`} onClick={() => (selectMode ? toggleSelect(job.id) : handleJobClick(job))} style={{ cursor: 'pointer' }}>
                {selectMode && <span className={`sel-box${selected ? ' on' : ''}`} aria-hidden="true">{selected && <IcCheck />}</span>}
                <div className="time">
                    <div className="hm num">{timeStr || '—'}</div>
                    {overdue && <div className="late">{t('jobs.late')}</div>}
                </div>
                <div className="body">
                    <div className="top">
                        <span className="cust">{job.client?.firstName} {job.client?.lastName}</span>
                        {overdue && <span className="flag late">{t('jobs.overdue')}</span>}
                        {needsReschedule(job) && <span className="flag late" style={{ marginLeft: overdue ? 6 : 'auto' }}>{MISC_LABELS.needsRescheduling}</span>}
                    </div>
                    <div className="jmeta">
                        {job.id != null && <span className="jno num">#{job.id}</span>}
                        {badgeNos.map(n => <span key={n} className="bno num">T {n}</span>)}
                        {!hasTimeAnchor(job) && <span className="badge-sbr b-grey">{MISC_LABELS.notScheduled}</span>}
                        {job.paymentStatus && (
                            <span className={`pill ${job.paymentStatus === 'PAID' ? 'paid' : 'due'}`}>
                                {job.paymentStatus === 'PAID' ? t('common.paid') : t('common.pending')}
                            </span>
                        )}
                        {isInvoiceOverdue(job) && <span className="pill due" title={t('jobs.paymentDueDatePassed')}>{t('jobs.invoiceOverdue')}</span>}
                    </div>
                    {(street || aptDetail) && (
                        <div className="loc">
                            <IcPin />
                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {street || aptDetail}
                                {street && aptDetail && <span className="apt"> · {aptDetail}</span>}
                            </span>
                        </div>
                    )}
                    <div className="foot">
                        <span className={`badge-sbr ${si.badge}`}>{si.label}</span>
                        <button type="button" className="job-send-invoice" onClick={(event) => openInvoice(event, job)}>{t('jobs.sendInvoice')}</button>
                        {assignee && assignee.fullName && (
                            <span className="who">
                                <Avatar className="a" size={24} name={assignee.fullName} src={assignee?.avatarUrl} />
                                <span className="nm">{assignee.fullName.split(' ')[0]} {(assignee.fullName.split(' ').pop() || '')[0]}.</span>
                            </span>
                        )}
                        <span className="price num">{rsdFormat(job.price, false)} <span className="price-sfx">RSD</span></span>
                    </div>
                </div>
                <IcChevR />
            </div>
        );
    };

    /* ── Desktop row ── */
    const DesktopJobRow = ({ job }) => {
        const si = statusInfo(job.status);
        const timeStr = fmtTime(getAnchorTime(job));
        const assignee = getPrimaryAssignee(job);
        const overdue = isOverdue(job);
        const badgeNos = Array.isArray(job.badges) ? job.badges.map(b => tagLabel(b)).filter(Boolean) : [];
        const street = job.client ? getCivicAddress(job.client) : '';
        const aptDetail = job.client ? (displayAddress1(job.client) || '') : '';
        const selected = selectMode && selectedIds.includes(job.id);
        return (
            <div className={`djob ${si.cls}${selected ? ' selected' : ''}`} onClick={() => (selectMode ? toggleSelect(job.id) : handleJobClick(job))} style={{ cursor: 'pointer' }}>
                {selectMode && <span className={`sel-box${selected ? ' on' : ''}`} aria-hidden="true">{selected && <IcCheck />}</span>}
                <div className="time">
                    <div className="hm num">{timeStr || '—'}</div>
                    <div className="dur">{job.status === 'COMPLETED' ? t('jobs.done') : ''}</div>
                </div>
                {job.id != null && <div className="jno num">#{job.id}</div>}
                <div className="cust">{job.client?.firstName} {job.client?.lastName}</div>
                <div className="loc">
                    <IcPin className="ic" />
                    <span className="st">{street || aptDetail || '—'}</span>
                    {street && aptDetail && <span className="apt">· {aptDetail}</span>}
                </div>
                {badgeNos.length > 0 && (
                    <span className="jbadges">
                        {badgeNos.slice(0, 2).map(n => <span key={n} className="bno num">T {n}</span>)}
                        {badgeNos.length > 2 && <span className="bno num">+{badgeNos.length - 2}</span>}
                    </span>
                )}
                <span className={`badge-sbr ${si.badge}`}>{si.label}</span>
                {!hasTimeAnchor(job) && <span className="badge-sbr b-grey">{MISC_LABELS.notScheduled}</span>}
                {job.paymentStatus && (
                    <span className={`pill ${job.paymentStatus === 'PAID' ? 'paid' : 'due'}`}>
                        {job.paymentStatus === 'PAID' ? t('common.paid') : t('common.pending')}
                    </span>
                )}
                {isInvoiceOverdue(job) && <span className="pill due" title={t('jobs.paymentDueDatePassed')}>{t('jobs.invoiceOverdue')}</span>}
                {overdue && <span className="flag late">{t('jobs.overdue')}</span>}
                {needsReschedule(job) && <span className="flag late" style={{ marginLeft: overdue ? 6 : 'auto' }}>{MISC_LABELS.needsRescheduling}</span>}
                {assignee && (
                    <div className="who">
                        <Avatar className="a" size={26} name={assignee.fullName} src={assignee?.avatarUrl} />
                        <span className="nm">{assignee.fullName}</span>
                    </div>
                )}
                <div className="price num">{rsdFormat(job.price)}</div>
                <button type="button" className="job-send-invoice" onClick={(event) => openInvoice(event, job)}>{t('jobs.sendInvoice')}</button>
            </div>
        );
    };

    /* ── Day section ── */
    const DaySection = ({ group, mobile }) => (
        <>
            <div className="daygroup">
                <span className="d">{dayLabel(group.date, t)}</span>
                <span className="dot" />
                <span className="meta">{fmtDateLabel(group.date)}</span>
                <span className="count">{t('jobs.job', { count: group.jobs.length })}</span>
            </div>
            {group.jobs.map(job =>
                mobile
                    ? <MobileJobCard key={job.id} job={job} />
                    : <DesktopJobRow key={job.id} job={job} />
            )}
        </>
    );

    const rescheduleView = attentionFilter === 'reschedule' || !!filter.needsReschedule;
    const EmptyState = () => (
        <div className="card-sbr" style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--muted)' }}>{rescheduleView ? t('jobs.nothingNeedsRescheduling') : t('jobs.noJobs')}</div>
            <div style={{ fontSize: 12.5, color: 'var(--faint)', marginTop: 4 }}>
                {rescheduleView ? t('jobs.everyJobHasNewTime') : t('jobs.tryAdjustingFilters')}
            </div>
        </div>
    );

    // Upcoming days → Unscheduled → (All jobs only) Earlier days, newest first.
    const JobList = ({ mobile }) => {
        const Row = mobile ? MobileJobCard : DesktopJobRow;
        const sectionCls = mobile ? 'daygroup' : 'sect';
        const labelCls = mobile ? 'd' : 't';
        if (sortedGroups.length === 0 && noTimeJobs.length === 0) return <EmptyState />;
        return (
            <>
                {upcomingGroups.map((g, i) => <DaySection key={`u${i}`} group={g} mobile={mobile} />)}
                {noTimeJobs.length > 0 && (
                    <>
                        <div className={sectionCls}>
                            <span className={labelCls}>{MISC_LABELS.notScheduled}</span>
                            <span className="dot" />
                            <span className="count">{noTimeJobs.length}</span>
                        </div>
                        {noTimeJobs.map(job => <Row key={job.id} job={job} />)}
                    </>
                )}
                {pastGroups.length > 0 && (
                    <div className={sectionCls} style={{ marginTop: 18 }}>
                        <span className={labelCls}>{t('jobs.earlier')}</span>
                        <span className="dot" />
                        <span className="count">{pastTotal}</span>
                    </div>
                )}
                {visiblePastGroups.map((g, i) => <DaySection key={`p${i}`} group={g} mobile={mobile} />)}
                {hiddenPastCount > 0 && (
                    <button
                        type="button"
                        className="btn-sbr btn-ghost-sbr"
                        onClick={() => setShowAllPast(true)}
                        style={{ width: '100%', justifyContent: 'center', borderStyle: 'dashed', padding: 12, marginTop: 8 }}
                    >
                        {t('jobs.showOlderJobs', { count: hiddenPastCount })}
                    </button>
                )}
            </>
        );
    };

    return (
        <div className="sb-page">
            <InvoiceSharePanel invoice={invoiceToSend} onClose={() => setInvoiceToSend(null)} />
            <AddToRouteDrawer show={routeDrawer} onHide={() => setRouteDrawer(false)} jobs={selectedJobs}
                initialDate={queryRouteDate} initialPlanId={queryPlan} onDone={exitSelectMode} />
            <ConfirmationModal display={confirmationModal} toggle={setConfirmationModal}
                title={t('jobs.deleteJob')} message={t('jobs.deleteJobConfirm')} confirmAction={handleDeleteJob} />
            <SetOrderModal display={orderModal} toggle={setOrderModal} />
            {selectedJob.id &&
                <SetTempEditOrderModal display={editJobModal} toggle={setEditJobModal} jobId={selectedJob.id}
                    setSelectedJob={setSelectedJob} alert={alert} setAlert={setAlert} />
            }

            <div className="sbr">

                {/* ── Desktop header ── */}
                <div className="dhead sbr-d">
                    <div>
                        <div className="ttl">{t('jobs.title')}</div>
                        <div className="sub">
                            {new Date().toLocaleDateString(dateLocale(), { weekday: 'long', day: 'numeric', month: 'long' })}
                            {filteredSorted.length > 0 && ` · ${t('jobs.job', { count: filteredSorted.length })} · ${rsdFormat(listTotal)} ${t('common.total').toLowerCase()}`}
                        </div>
                    </div>
                    <div className="act" style={{ display: 'flex', gap: 8 }}>
                        <button className={`btn-sbr btn-sm-sbr ${selectMode ? 'btn-primary-sbr' : 'btn-ghost-sbr'}`} onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}>
                            <IcRoute /> {selectMode ? t('jobs.doneSelecting') : t('jobs.planRoute')}
                        </button>
                        <button className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => setOrderModal(true)}>
                            <IcPlus /> {t('jobs.newJob')}
                        </button>
                    </div>
                </div>

                {/* ── Mobile header ── (inner .jobs-mhead is the flex row — the
                    .sbr-m wrapper forces display:block, so the row lives one level in.
                    Filters + New sit together on a single line beside the title.) */}
                <div className="sbr-m">
                    <div className="jobs-mhead">
                        <div className="titles">
                            <div className="ttl-m">{t('jobs.title')}</div>
                            <div className="sub-m">
                                {new Date().toLocaleDateString(dateLocale(), { weekday: 'long', day: 'numeric', month: 'long' })}
                                {filteredSorted.length > 0 && ` · ${rsdFormat(listTotal)}`}
                            </div>
                        </div>
                        <div className="acts-m">
                            <JobsFilter value={filter} onFilter={setFilter} isFiltering={isFetching} />
                            <button className={`btn-sbr btn-sm-sbr ${selectMode ? 'btn-primary-sbr' : 'btn-ghost-sbr'}`} onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))} aria-label={selectMode ? t('jobs.doneSelecting') : t('jobs.planRoute')}><IcRoute /></button>
                            <button className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => setOrderModal(true)}><IcPlus /> {t('jobs.newShort')}</button>
                        </div>
                    </div>
                </div>

                {/* ── Filter (desktop only — mobile shows it in the header row above) ── */}
                <div className="sbr-d" style={{ marginBottom: 14 }}>
                    <JobsFilter value={filter} onFilter={setFilter} isFiltering={isFetching} />
                </div>

                {/* ── Client context (from a client's "Total Jobs" link) ── */}
                {queryClientName && (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', marginBottom: 14,
                        background: 'var(--brand-50)', border: '1px solid var(--brand-100)', borderRadius: 'var(--r-sm, 10px)'
                    }}>
                        <IcPeople />
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>
                            {t('jobs.showingAllJobsFor')} <span style={{ color: 'var(--brand)' }}>{queryClientName}</span>
                        </span>
                        <button
                            type="button"
                            onClick={clearClientContext}
                            style={{
                                marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5,
                                background: '#fff', border: '1px solid var(--brand-100)', borderRadius: 8,
                                color: 'var(--brand)', fontWeight: 700, fontSize: 12.5, padding: '5px 10px', cursor: 'pointer'
                            }}
                        >
                            {t('jobs.clear')}
                            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                        </button>
                    </div>
                )}

                {/* ── Unpaid context (from the Overview "unpaid invoices" card) ── */}
                {paymentFilter === 'PENDING' && (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', marginBottom: 14,
                        background: 'var(--warn-bg)', border: '1px solid var(--warn-bd)', borderRadius: 'var(--r-sm, 10px)'
                    }}>
                        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="var(--warn)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>
                            {t('jobs.showing')} <span style={{ color: 'var(--warn)' }}>{unpaidJobs.length} {t('common.unpaid').toLowerCase()} {t('jobs.job', { count: unpaidJobs.length }).split(' ').pop()}</span>
                            {unpaidJobs.length > 0 && <> · {t('jobs.outstanding', { amount: rsdFormat(unpaidTotal) })}</>}
                        </span>
                        <button
                            type="button"
                            onClick={clearPaymentContext}
                            style={{
                                marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5,
                                background: '#fff', border: '1px solid var(--warn-bd)', borderRadius: 8,
                                color: 'var(--warn)', fontWeight: 700, fontSize: 12.5, padding: '5px 10px', cursor: 'pointer'
                            }}
                        >
                            {t('jobs.clear')}
                            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                        </button>
                    </div>
                )}

                {/* ── Attention context (from an Overview "Needs your attention" card) ── */}
                {attentionFilter && (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', marginBottom: 14,
                        background: 'var(--brand-50)', border: '1px solid var(--brand-100)', borderRadius: 'var(--r-sm, 10px)'
                    }}>
                        <IcWarn />
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>
                            {t('jobs.showing')} <span style={{ color: 'var(--brand)' }}>
                                {t(ATTENTION_I18N[attentionFilter].label, { count: filteredSorted.length })}
                            </span> — {t(ATTENTION_I18N[attentionFilter].hint)}
                        </span>
                        <button
                            type="button"
                            onClick={clearAttentionContext}
                            style={{
                                marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5,
                                background: '#fff', border: '1px solid var(--brand-100)', borderRadius: 8,
                                color: 'var(--brand)', fontWeight: 700, fontSize: 12.5, padding: '5px 10px', cursor: 'pointer'
                            }}
                        >
                            {t('jobs.clear')}
                            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                        </button>
                    </div>
                )}

                {/* ── Overdue alert — click to filter the list to overdue pickups only ── */}
                {overdueJobs.length > 0 && (
                    <div className="attn" onClick={toggleOverdueView} style={{ cursor: 'pointer' }}>
                        <div className="ico"><IcWarn /></div>
                        <div className="tx">
                            <b>{t('jobs.pickupsOverdue', { count: overdueJobs.length })}</b>
                            <span>
                                {overdueOnly
                                    ? t('jobs.showingOverdueOnly')
                                    : <>
                                        {overdueJobs[0].client?.firstName} {overdueJobs[0].client?.lastName}
                                        {overdueJobs.length > 1 ? ` ${t('jobs.andMore', { count: overdueJobs.length - 1 })}` : ''} — {t('jobs.pastPickupTime')}
                                    </>
                                }
                            </span>
                        </div>
                        <div className="go">{overdueOnly ? `${t('jobs.clear')} ×` : `${t('common.view')} ›`}</div>
                    </div>
                )}

                {/* ── Route selection bar ── */}
                {selectMode && (
                    <div className="sel-bar">
                        <IcRoute />
                        <span className="tx">
                            <b>{selectedIds.length}</b> {t('jobs.job', { count: selectedIds.length })} {t('jobs.selected')}
                            {queryRouteDate && <span className="sub"> · {t('jobs.forDate', { date: queryRouteDate })}</span>}
                            {selectedIds.length === 0 && <span className="sub"> · {t('jobs.tapJobsForRoute')}</span>}
                        </span>
                        <div className="sp">
                            <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={exitSelectMode}>{t('common.cancel')}</button>
                            <button type="button" className="btn-sbr btn-primary-sbr btn-sm-sbr" disabled={!selectedIds.length} onClick={() => setRouteDrawer(true)}>
                                {t('jobs.addToRoute')}
                            </button>
                        </div>
                    </div>
                )}

                {/* ── Search ── */}
                <div className="jobs-search">
                    <svg className="ic" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('jobs.searchPlaceholder')}
                        aria-label={t('jobs.searchJobs')}
                    />
                    {search && (
                        <button className="clear" type="button" onClick={() => setSearch('')} aria-label={t('jobs.clearSearch')}>
                            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                    )}
                </div>

                {/* ── Status chips ── */}
                <div className="seg">
                    {CHIP_KEYS.map(key => (
                        <div
                            key={key}
                            className={`chip${activeChip === key ? ' active' : ''}`}
                            onClick={() => setActiveChip(key)}
                        >
                            {key === 'all' ? t('common.all') : getStatusMeta(key).label}
                            {chipCounts[key] > 0 && <span className="c">{chipCounts[key]}</span>}
                        </div>
                    ))}
                </div>

                {/* ── Loading ── */}
                {isGlobalLoading && (
                    <div className="card-sbr"><SkeletonRows rows={6} widths={['65%', '42%']} /></div>
                )}

                {/* ── Mobile list ── */}
                {!isGlobalLoading && (
                    <div className="sbr-m">
                        <JobList mobile />
                    </div>
                )}

                {/* ── Desktop: main list + right rail ── */}
                {!isGlobalLoading && (
                    <div className="sbr-d">
                        <div className="cols" style={{ gridTemplateColumns: 'minmax(0,1fr) 296px' }}>

                            {/* Main column */}
                            <div>
                                <JobList mobile={false} />
                            </div>

                            {/* Right rail */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                                {/* Today at a glance */}
                                <div className="card-sbr" style={{ margin: 0 }}>
                                    <div className="ch">
                                        <svg className="ci" viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--brand)', flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                        <span className="t">{t('jobs.todayAtAGlance')}</span>
                                    </div>
                                    <div className="row-sbr">
                                        <div className="info"><div className="nm" style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>{t('jobs.jobsScheduled')}</div></div>
                                        <div className="rt"><div className="v num">{todayAll.length}</div></div>
                                    </div>
                                    <div className="row-sbr">
                                        <div className="info"><div className="nm" style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>{t('common.completed')}</div></div>
                                        <div className="rt"><div className="v num">{todayDone.length}</div></div>
                                    </div>
                                    <div className="row-sbr">
                                        <div className="info"><div className="nm" style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>{t('jobs.todaysValue')}</div></div>
                                        <div className="rt"><div className="v num" style={{ fontSize: 13 }}>{rsdFormat(todayValue)}</div></div>
                                    </div>
                                    {todayUnpaid > 0 && (
                                        <div className="row-sbr">
                                            <div className="info"><div className="nm" style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>{t('common.unpaid')}</div></div>
                                            <div className="rt"><div className="v num" style={{ fontSize: 13, color: 'var(--warn)' }}>{rsdFormat(todayUnpaid)}</div></div>
                                        </div>
                                    )}
                                </div>

                                {/* Team on shift */}
                                {teamOnShift.length > 0 && (
                                    <div className="card-sbr" style={{ margin: 0 }}>
                                        <div className="ch">
                                            <svg className="ci" viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--brand)', flexShrink: 0 }}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                                            <span className="t">{t('jobs.teamOnShift')}</span>
                                        </div>
                                        {teamOnShift.map(person => (
                                            <div key={person.id} className="row-sbr">
                                                <Avatar className="a" size={38} name={person.fullName} src={person?.avatarUrl} />
                                                <div className="info">
                                                    <div className="nm">{person.fullName}</div>
                                                    <div className="sub">{person.roles?.[0]?.role ? roleLabel(person.roles[0].role) : roleLabel('staff')}</div>
                                                </div>
                                                <div className="rt">
                                                    <div className="v num" style={{ fontSize: 13 }}>{t('jobs.job', { count: person.count })}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* New job shortcut */}
                                <button
                                    className="btn-sbr btn-ghost-sbr"
                                    onClick={() => setOrderModal(true)}
                                    style={{ width: '100%', justifyContent: 'center', borderStyle: 'dashed', padding: 13 }}
                                >
                                    <IcPlus /> {t('jobs.newJob')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default JobsPage;
